import React, { useState, useEffect } from 'react';
import { fetchWorkOrders } from './api'; // Adjust the import path as necessary
import './archived.css';
import WorkOrderModal from './workordermodal';
import NewWorkOrderModal from './newworkordermodal';
import PaymentBadges from './PaymentBadges';

// Local YYYY-MM-DD for a Date.
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Date range for a quick pick (weeks start Monday). Empty = no limit.
const dateRange = (preset, customFrom, customTo) => {
  const today = new Date();
  const day = (offset) => new Date(today.getFullYear(), today.getMonth(), today.getDate() + offset);
  const monday = day(-((today.getDay() + 6) % 7));
  switch (preset) {
    case 'this_week': return { from: isoDay(monday), to: isoDay(today) };
    case 'last_week': return { from: isoDay(new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() - 7)),
                                to: isoDay(new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() - 1)) };
    case 'this_month': return { from: isoDay(new Date(today.getFullYear(), today.getMonth(), 1)), to: isoDay(today) };
    case 'last_month': return { from: isoDay(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
                                 to: isoDay(new Date(today.getFullYear(), today.getMonth(), 0)) };
    case 'last_30': return { from: isoDay(day(-29)), to: isoDay(today) };
    case 'custom': return { from: customFrom, to: customTo };
    default: return { from: '', to: '' };
  }
};

const formatCreated = (dateTime) => (dateTime ? new Date(dateTime).toLocaleDateString() : '');

const Archived = () => {
  const [workOrders, setWorkOrders] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0); // Bumped to reload the list after a change
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedService, setSelectedService] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedBilling, setSelectedBilling] = useState('all');
  // Sorting and the created-date filter run on the server, so they cover every page.
  const [sortOrder, setSortOrder] = useState('newest');
  const [createdPreset, setCreatedPreset] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  // Billed = paid
  const [billedPreset, setBilledPreset] = useState('all');
  const [billedFrom, setBilledFrom] = useState('');
  const [billedTo, setBilledTo] = useState('');
  const [pageSize, setPageSize] = useState(50);
  const [showModal, setShowModal] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [showNewWorkOrderModal, setShowNewWorkOrderModal] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationInfo, setPaginationInfo] = useState({});

  // Format date without year for mobile view
  const formatDateWithoutYear = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${month}/${day}`;
  };

  const toggleNewWorkOrderModal = () => {
    setShowNewWorkOrderModal(!showNewWorkOrderModal);
  };

  const handleWorkOrderCreated = (newWorkOrder) => {
    setWorkOrders(prev => [...prev, newWorkOrder]);
    setShowNewWorkOrderModal(false);
    refreshWorkOrders();
  };

  // Reload the current page in place after a work order is created or changed
  const refreshWorkOrders = () => setRefreshKey(key => key + 1);

  const toggleService = (service) => {
    setSelectedService(service);
  };

  const toggleStatus = (status) => {
    setSelectedStatus(selectedStatus === status ? '' : status);
  };

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const handleWorkOrderClick = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setShowModal(true);
  };

  const handleWorkOrderUpdate = (updatedWorkOrder) => {
    // If work order is no longer archived, remove it from the archived list
    if (!updatedWorkOrder.is_archived) {
      setWorkOrders(prev => prev.filter(wo => wo.id !== updatedWorkOrder.id));
    } else {
      // If work order is still archived, update it in the list
      setWorkOrders(prev => prev.map(wo => wo.id === updatedWorkOrder.id ? updatedWorkOrder : wo));
    }
    refreshWorkOrders();
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleNextPage = () => {
    if (paginationInfo.hasNext) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (paginationInfo.hasPrevious) {
      setCurrentPage(currentPage - 1);
    }
  };

  useEffect(() => {
    const loadWorkOrders = async () => {
      // Convert "All" to empty string for API call
      const serviceFilter = selectedService === 'All' ? '' : selectedService;
      const created = dateRange(createdPreset, customFrom, customTo);
      const billed = dateRange(billedPreset, billedFrom, billedTo);
      const fetchedWorkOrders = await fetchWorkOrders(serviceFilter, '', searchQuery, selectedStatus, true, currentPage, pageSize,
        selectedBilling, { ordering: sortOrder, created_from: created.from, created_to: created.to,
                           billed_from: billed.from, billed_to: billed.to });

      if (fetchedWorkOrders && fetchedWorkOrders.results) {
        setWorkOrders(fetchedWorkOrders.results);
        setTotalPages(fetchedWorkOrders.total_pages || 1);
        setTotalCount(fetchedWorkOrders.count || 0);
        setPaginationInfo({
          hasNext: fetchedWorkOrders.has_next || false,
          hasPrevious: fetchedWorkOrders.has_previous || false,
          nextPage: fetchedWorkOrders.next_page || null,
          previousPage: fetchedWorkOrders.previous_page || null
        });
      } else {
        // Handle case where response doesn't have expected structure
        setWorkOrders([]);
        setTotalPages(1);
        setTotalCount(0);
        setPaginationInfo({
          hasNext: false,
          hasPrevious: false,
          nextPage: null,
          previousPage: null
        });
      }
    };

    loadWorkOrders();
  }, [selectedService, searchQuery, selectedStatus, selectedBilling, sortOrder, createdPreset, customFrom, customTo, billedPreset, billedFrom, billedTo, pageSize, currentPage, refreshKey]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedService, searchQuery, selectedStatus, selectedBilling, sortOrder, createdPreset, customFrom, customTo, billedPreset, billedFrom, billedTo, pageSize]);

  return (
    <div className='aworkorderlist'>
      <div className='aworkorderlistfilters'>
        <button className='anewworkorderbutton' onClick={toggleNewWorkOrderModal}>
          <span className='anewworkorderbuttontext'>New Work Order</span>
          <span className='anewworkorderbuttonplus'>+</span>
        </button>
        <div className='aworkorderlistfiltersrow'>
          <div className='astatusfilter'>
            <label className='astatuslabel'>
              <span className='aworkorderlistlabeltext'>Status:</span>
              <select className='astatusselect' value={selectedStatus} onChange={(e) => toggleStatus(e.target.value)}>
                <option value="">All</option>
                <option value="In Process">In Process</option>
                <option value="Called">Called</option>
                <option value="Picked Up">Picked Up</option>
              </select>
            </label>
          </div>
          <div className='astatusfilter'>
            <label className='astatuslabel'>
              <span className='aworkorderlistlabeltext'>Service:</span>
              <select className='astatusselect' value={selectedService} onChange={(e) => toggleService(e.target.value)}>
                <option value="All">All</option>
                <option value="Restoration">Restoration</option>
                <option value="Straightening">Straightening</option>
                <option value="Repair Crack">Repair Crack</option>
                <option value="Powder Coating">Powder Coating</option>
                <option value="Mount & Balance">Mount & Balance</option>
                <option value="Other">Other</option>
              </select>
            </label>
          </div>
          <div className='astatusfilter'>
            <label className='astatuslabel'>
              <span className='aworkorderlistlabeltext'>Billing:</span>
              <select className='astatusselect' value={selectedBilling} onChange={(e) => setSelectedBilling(e.target.value)}>
                <option value="all">All Billing</option>
                <option value="not_invoiced">Not Invoiced</option>
                <option value="invoiced">Invoiced (Unpaid)</option>
                <option value="paid">Paid</option>
              </select>
            </label>
          </div>
          <div className='astatusfilter'>
            <label className='astatuslabel'>
              <span className='aworkorderlistlabeltext'>Created:</span>
              <select className='astatusselect' value={createdPreset} onChange={(e) => setCreatedPreset(e.target.value)}>
                <option value="all">Any Created Date</option>
                <option value="this_week">Created This Week</option>
                <option value="last_week">Created Last Week</option>
                <option value="this_month">Created This Month</option>
                <option value="last_month">Created Last Month</option>
                <option value="last_30">Created Last 30 Days</option>
                <option value="custom">Custom Range…</option>
              </select>
            </label>
          </div>
          {createdPreset === 'custom' && (
            <>
              <label className='astatuslabel'>
                <span className='aworkorderlistlabeltext'>From:</span>
                <input className='astatusselect' type='date' value={customFrom} max={customTo || undefined}
                  onChange={(e) => setCustomFrom(e.target.value)} aria-label='Created from' />
              </label>
              <label className='astatuslabel'>
                <span className='aworkorderlistlabeltext'>To:</span>
                <input className='astatusselect' type='date' value={customTo} min={customFrom || undefined}
                  onChange={(e) => setCustomTo(e.target.value)} aria-label='Created to' />
              </label>
            </>
          )}
          <div className='astatusfilter'>
            <label className='astatuslabel'>
              <span className='aworkorderlistlabeltext'>Billed:</span>
              <select className='astatusselect' value={billedPreset} onChange={(e) => setBilledPreset(e.target.value)}>
                <option value="all">Any Billed Date</option>
                <option value="this_week">Billed This Week</option>
                <option value="last_week">Billed Last Week</option>
                <option value="this_month">Billed This Month</option>
                <option value="last_month">Billed Last Month</option>
                <option value="last_30">Billed Last 30 Days</option>
                <option value="custom">Custom Range…</option>
              </select>
            </label>
          </div>
          {billedPreset === 'custom' && (
            <>
              <label className='astatuslabel'>
                <span className='aworkorderlistlabeltext'>From:</span>
                <input className='astatusselect' type='date' value={billedFrom} max={billedTo || undefined}
                  onChange={(e) => setBilledFrom(e.target.value)} aria-label='Billed from' />
              </label>
              <label className='astatuslabel'>
                <span className='aworkorderlistlabeltext'>To:</span>
                <input className='astatusselect' type='date' value={billedTo} min={billedFrom || undefined}
                  onChange={(e) => setBilledTo(e.target.value)} aria-label='Billed to' />
              </label>
            </>
          )}
          <div className='astatusfilter'>
            <label className='astatuslabel'>
              <span className='aworkorderlistlabeltext'>Sort:</span>
              <select className='astatusselect' value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="billed_desc">Billed (Newest)</option>
                <option value="billed">Billed (Oldest)</option>
              </select>
            </label>
          </div>
          <div className='astatusfilter'>
            <label className='astatuslabel'>
              <span className='aworkorderlistlabeltext'>Show:</span>
              <select className='astatusselect' value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
                <option value={250}>250 per page</option>
              </select>
            </label>
          </div>
        </div>
        <label className='astatuslabel aworkorderlistsearchlabel'>
          <span className='aworkorderlistlabeltext'>Search:</span>
          <input
            className='asearchbar'
            type="text"
            placeholder="⌕ Search Work Orders"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </label>
      </div>
      <button className='aworkorderlistmobilebutton' onClick={toggleNewWorkOrderModal}>
        <span className='anewworkorderbuttonplus'>+</span>
      </button>
      <div className='aworkorderlistlabelsparent'>
        <div className='aworkorderlistlabels'>
          <h5 className='aworkorderlistlabelsheaders'>
            <span className='aworkorderlistlabeldesktop'>Customer Name</span>
            <span className='aworkorderlistlabelmobile'>Customer</span>
          </h5>
          <h5 className='aworkorderlistlabelsheaders'>
            <span className='aworkorderlistlabeldesktop'>Vehicle Make</span>
            <span className='aworkorderlistlabelmobile'>Vehicle</span>
          </h5>
          <h5 className='aworkorderlistlabelsheaders'>Service</h5>
          <h5 className='aworkorderlistlabelsheaders'>Created</h5>
          <h5 className='aworkorderlistlabelsheaders'>
            <span className='aworkorderlistlabeldesktop'>Date Promised</span>
            <span className='aworkorderlistlabelmobile'>Due</span>
          </h5>
          <h5 className='aworkorderlistlabelsheaders'>
            <span className='aworkorderlistlabeldesktop'>Work Order #</span>
            <span className='aworkorderlistlabelmobile'>WO #</span>
          </h5>
        </div>
        <hr className='aworkorderlistlabelsheadersline'/>
      </div>
      <div className='aworkorderlistscroll'>
        {workOrders && workOrders.length > 0 ? (
          // Already in the chosen order from the server (across all pages)
          workOrders.map((order) => (
              <div className='aworkorderlistrow' key={order.id} onClick={() => handleWorkOrderClick(order)}>
                <div className='aworkorderlistrowdataname'>
                  {order.customer_name}
                  <PaymentBadges workOrder={order} />
                </div>
                <div className='aworkorderlistrowdatavehicle'>{order.vehicle}</div>
                <div className='aworkorderlistrowdataservice'>{order.service_requested}</div>
                <div className='aworkorderlistrowdatadate'>
                  <span className='aworkorderlistlabeldesktop'>{formatCreated(order.created_at)}</span>
                  <span className='aworkorderlistlabelmobile'>{formatDateWithoutYear(order.created_at)}</span>
                </div>
                <div className='aworkorderlistrowdatadate'>
                  <span className='aworkorderlistlabeldesktop'>{order.date_promised}</span>
                  <span className='aworkorderlistlabelmobile'>{formatDateWithoutYear(order.date_promised)}</span>
                </div>
                <div className='aworkorderlistrowdatadate'>{order.id}</div>
              </div>
            ))
        ) : (
          <p>No work orders found</p>
        )}

        {totalPages <= 1 && totalCount > 0 && (
          <div className="aworkorderlist-pagination-info">{totalCount} archived work order{totalCount === 1 ? '' : 's'}</div>
        )}
        
        {/* Pagination */}
        {totalPages > 1 && (
        <div className="aworkorderlist-pagination">
          <div className="aworkorderlist-pagination-info">
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} archived work orders
          </div>
          <div className="aworkorderlist-pagination-controls">
            <button 
              className="aworkorderlist-pagination-btn"
              onClick={handlePreviousPage}
              disabled={!paginationInfo.hasPrevious}
            >
              ← Previous
            </button>
            
            <div className="aworkorderlist-pagination-pages">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    className={`aworkorderlist-pagination-page ${
                      pageNum === currentPage ? 'active' : ''
                    }`}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button 
              className="aworkorderlist-pagination-btn"
              onClick={handleNextPage}
              disabled={!paginationInfo.hasNext}
            >
              Next →
            </button>
          </div>
        </div>
        )}
      </div>
      <NewWorkOrderModal
        showModal={showNewWorkOrderModal}
        toggleModal={toggleNewWorkOrderModal}
        onWorkOrderCreated={handleWorkOrderCreated}
      />
      <WorkOrderModal 
        showModal={showModal} 
        toggleModal={toggleModal} 
        workOrder={selectedWorkOrder}
        onWorkOrderUpdated={handleWorkOrderUpdate}
      />
    </div>
  );
};

export default Archived;